import { z } from "zod";

/**
 * VideoResolution / VideoFps / VideoFormat mirror cameraPlan.ts's encoder
 * vocabulary (cameraResolutionSchema / cameraFpsSchema / cameraOutputFormatSchema)
 * as the bare string-literal unions. They are declared here rather than imported
 * because cameraPlan.ts imports this module's videoPresetSchema + qualitySchema —
 * importing back, even type-only, would close a cycle the leaf/orchestrator matrix
 * forbids. cameraOutputSchema is the single runtime source for these values; this
 * module only seeds presets + indexes a bitrate table, so it carries the literals
 * directly and lets cameraOutputSchema's parse remain the one validation boundary.
 */
type VideoResolution = "720p" | "1080p" | "1440p" | "2160p";
type VideoFps = 24 | 30 | 60;
type VideoFormat = "mp4" | "webm-vp9" | "webm-vp8";

/**
 * videoPresetSchema names the framing preset the wizard's first choice applies.
 * `vertical` (9:16), `widescreen` (16:9), and `square` (1:1) are the three
 * ready-made platform-true templates — same 1080p H.264 30 fps recipe, differing
 * only in frame shape; `custom` is the escape hatch that reveals the full encoder
 * matrix (format / resolution / fps / quality). Zod-first because the value is
 * stamped onto the Camera Plan's output and shared with the preset-set tool input.
 * It lives here rather than in cameraPlan.ts so the preset templates and the plan
 * that imports them share one source without a runtime cycle — cameraPlan.ts
 * imports this schema, never the other way around.
 */
export const videoPresetSchema = z.enum(["vertical", "widescreen", "square", "custom"]);

/**
 * VideoPreset names the framing preset, derived from videoPresetSchema.
 */
export type VideoPreset = z.infer<typeof videoPresetSchema>;

/**
 * qualitySchema names the encoder quality tier the Custom matrix exposes — `low`,
 * `medium`, or `high` — which, paired with the resolution and fps, picks the
 * bitrate from bitrateForOutput rather than letting the user type a raw Mbps.
 * Zod-first because it is stamped onto the plan's output and shared with the
 * output-set tool input. Defined here alongside the bitrate table it indexes; like
 * videoPresetSchema, cameraPlan.ts imports it from this module so the two stay free
 * of a runtime cycle.
 */
export const qualitySchema = z.enum(["low", "medium", "high"]);

/**
 * Quality names the encoder quality tier, derived from qualitySchema.
 */
export type Quality = z.infer<typeof qualitySchema>;

/**
 * VideoPresetTemplate is the encoder-output seed a ready-made preset stamps onto
 * the Camera Plan's output: the container `format`, the vertical `resolution`, the
 * frame `aspect`, the `fps`, and the `bitrateMbps` the preset hard-codes (a preset
 * is a fixed recipe, so its bitrate is fixed rather than derived from a quality
 * tier). The fields are exactly the cameraOutput fields the preset-set tool merges
 * over the current output, so applying a template is a single spread.
 */
export interface VideoPresetTemplate {
  aspect: "16:9" | "9:16" | "1:1";
  bitrateMbps: number;
  format: VideoFormat;
  fps: VideoFps;
  resolution: VideoResolution;
}

/**
 * VIDEO_PRESETS holds the three ready-made platform-true templates the wizard
 * offers before the Custom matrix, keyed by the preset enum's non-custom members
 * (the Exclude keeps the keys in lockstep with videoPresetSchema). Every template
 * is the same broadcast-ready recipe — 1080p H.264 at 30 fps, 12 Mbps (the
 * 1080p/30/high row of BITRATE_TABLE_MBPS) — differing only in frame shape:
 * `vertical` 9:16 (Stories, Reels, Shorts, TikTok), `widescreen` 16:9 (YouTube and
 * the web), and `square` 1:1 (feed posts). `custom` carries no template (it reveals
 * the full matrix instead), so only the three platform presets appear here. The
 * preset-set tool spreads the chosen template over the plan's output and stamps the
 * preset name.
 */
export const VIDEO_PRESETS: Record<Exclude<VideoPreset, "custom">, VideoPresetTemplate> = {
  square: { aspect: "1:1", bitrateMbps: 12, format: "mp4", fps: 30, resolution: "1080p" },
  vertical: { aspect: "9:16", bitrateMbps: 12, format: "mp4", fps: 30, resolution: "1080p" },
  widescreen: { aspect: "16:9", bitrateMbps: 12, format: "mp4", fps: 30, resolution: "1080p" },
};

/**
 * BITRATE_TABLE_MBPS maps each resolution+fps combination to its [low, medium,
 * high] bitrate tier in Mbps. The values rise with resolution, frame rate, and
 * quality; 2160p/60/high tops out at 45 Mbps (the bound cameraOutputSchema's
 * bitrateMbps.max enforces). Held as a const lookup so bitrateForOutput is a single
 * indexed read rather than a branch ladder.
 */
const BITRATE_TABLE_MBPS: Record<VideoResolution, Record<VideoFps, [number, number, number]>> = {
  "720p": { 24: [2, 4, 6], 30: [2, 4, 6], 60: [3, 6, 9] },
  "1080p": { 24: [4, 8, 12], 30: [4, 8, 12], 60: [6, 12, 18] },
  "1440p": { 24: [8, 16, 20], 30: [8, 16, 20], 60: [12, 24, 30] },
  "2160p": { 24: [14, 22, 32], 30: [14, 22, 32], 60: [20, 32, 45] },
};

/**
 * QUALITY_TIER_INDEX maps a quality tier to its column in BITRATE_TABLE_MBPS's
 * [low, medium, high] triple, so bitrateForOutput resolves the bitrate without a
 * per-tier branch.
 */
const QUALITY_TIER_INDEX: Record<Quality, 0 | 1 | 2> = { high: 2, low: 0, medium: 1 };

/**
 * bitrateForOutput returns the recommended encoder bitrate in Mbps for a
 * resolution + frame rate + quality tier, read straight from BITRATE_TABLE_MBPS.
 * The Custom matrix shows this derived value (rather than letting the user type a
 * raw Mbps) so the bitrate always matches the chosen fidelity; 24 fps shares the
 * 30 fps row (the encoder treats the two as one rate tier). The returned value
 * stays within cameraOutputSchema's bitrateMbps bounds, with 2160p/60/high at the
 * 45 Mbps ceiling.
 */
export const bitrateForOutput = (
  resolution: VideoResolution,
  fps: VideoFps,
  quality: Quality,
): number => BITRATE_TABLE_MBPS[resolution][fps][QUALITY_TIER_INDEX[quality]];

/**
 * estimatedVideoSizeMb estimates the encoded file size in decimal megabytes for a
 * constant-bitrate recording: megabits per second × seconds ÷ 8 (8 bits to a byte).
 * It is the figure the review step shows beside the chosen encoder settings — a
 * rough "this clip is about N MB" from the bitrate and the flight's wall-clock
 * length, not a byte-exact prediction (the variable-bitrate encoder lands close to
 * but not exactly on the target). Pure, so the review surface recomputes it from the
 * plan alone.
 */
export const estimatedVideoSizeMb = (bitrateMbps: number, durationMs: number): number =>
  (bitrateMbps * (durationMs / 1_000)) / 8;

/**
 * aspectRatio returns the numeric width/height ratio for a frame aspect — 16:9
 * (landscape), 9:16 (portrait), or 1:1 (square) — so the React preview surface can
 * size its AspectRatio box to whatever the plan's output targets (a portrait preset
 * frames the map 9:16, not the landscape default). A pure mapping with no encoder
 * side, kept beside the presets that set the aspect.
 */
export const aspectRatio = (aspect: "16:9" | "9:16" | "1:1"): number => {
  if (aspect === "9:16") {
    return 9 / 16;
  }

  if (aspect === "1:1") {
    return 1;
  }

  return 16 / 9;
};

/**
 * SUGGESTED_LENGTH_MIN_MS and SUGGESTED_LENGTH_MAX_MS bound the suggested flight
 * length to the wizard's slider window (20 s to 120 s), the same bounds
 * cameraPlanSchema's targetDurationMs enforces. suggestVideoLengthMs clamps to this
 * range so a very short or very long route never proposes a duration the plan would
 * reject.
 */
const SUGGESTED_LENGTH_MIN_MS = 20_000;
const SUGGESTED_LENGTH_MAX_MS = 120_000;

/**
 * suggestVideoLengthMs proposes a flight duration (ms) for a route from its length:
 * roughly one second per kilometre plus a 10 s establishing cushion, rounded to
 * whole seconds, then clamped to the slider window [20 s, 120 s]. It is the
 * "Suggested" default the length control seeds — a 30 km route lands near 40 s, a
 * 200 km route saturates at the 120 s ceiling, and any sub-10 km route floors at
 * 20 s. Pure, so the control and any preview can recompute it from the route alone.
 */
export const suggestVideoLengthMs = (routeLengthMeters: number): number => {
  const seconds = Math.round(routeLengthMeters / 1_000 + 10);

  return Math.min(Math.max(seconds * 1_000, SUGGESTED_LENGTH_MIN_MS), SUGGESTED_LENGTH_MAX_MS);
};
